<template>
  <form @submit.prevent="submit" class="comment-input">
    <Tabs
      theme="border"
      v-model="data.isInternal"
      :items="[{ id: false }, { id: true, disabled: !$auth.isAuthenticated || !showInternal }]"
    >
      <template #false-label>
        {{ $tc('message', 1) }}
        <small class="text--small row middle g-1 ml-1">
          <Keyboard value="control" label="ctrl" />
          <span>+</span>
          <Keyboard value="1" />
        </small>
      </template>
      <template #true-label>
        {{ $t('note') }}
        <small class="text--small row middle g-1 ml-1">
          <Keyboard value="control" label="ctrl" />
          <span>+</span>
          <Keyboard value="2" />
        </small>
      </template>
      <template #before>
        <InputComponent>
          <template #default>
            <textarea
              v-model="data.message"
              v-auto-height
              @keydown.enter="onKeyEnter"
              autofocus
              :placeholder="$t('typeHere') + '...'"
              rows="3"
              class="elder-input__element"
              ref="input"
            ></textarea>
          </template>
          <template #below>
            <DropdownComponent class="comment-input__attachments" placement="top-start" :close-on-click="false">
              <template #default>
                <div class="row g-1 middle">
                  <ButtonComponent :label="$tc('attachment', 2)" :icon="['fal', 'file-upload']" class="text--small" />
                  <small class="fade" v-if="data.attachments.length">{{ attachmentCountHint }}</small>
                </div>
              </template>
              <template #dropdown>
                <FileComponent v-model="data.attachments" :label="$tc('attachment', 2)" multiple />
              </template>
            </DropdownComponent>
            <div class="comment-input__actions">
              <slot name="actions" />
              <ButtonComponent
                type="submit"
                :icon="['fal', 'paper-plane']"
                :disabled="!validator.passes"
                :label="$t('send')"
                :success-label="$t('sent')"
                :promise="promise"
                @onSuccess="onSuccess"
                theme="primary"
                class="comment-input__submit"
              />
            </div>
          </template>
        </InputComponent>
        <slot name="before" />
      </template>
      <template #after>
        <slot name="after" />
      </template>
    </Tabs>
  </form>
</template>

<script>
import autoHeight from '@/directives/auto-height'
import { mapi18n } from '@/i18n'
import Model from '@/mixins/Model'
import Validator from '@/mixins/validator-v2'
import Shortkey from '@/mixins/Shortkey'

import Tabs from '@/components/Tabs'
import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { FileComponent } from '@kvass/file'
import Keyboard from '@/components/Keyboard'

export default {
  mixins: [
    Model({
      source: undefined,
      model: { message: '', attachments: [], isInternal: false },
    }),
    Validator({
      rules: { message: 'required' },
      labels: 'labels',
      data: 'data',
    }),
    Shortkey({
      'Control+1': ctx => (ctx.data.isInternal = false),
      'Control+2': ctx => (ctx.data.isInternal = true),
    }),
  ],
  directives: { autoHeight },
  props: {
    promise: Promise,
    showInternal: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    'data.isInternal': {
      handler: 'focus',
      immediate: true,
    },
  },
  computed: {
    ...mapi18n('labels', {
      message: ['message', 1],
    }),
    attachmentCountHint() {
      return `${this.data.attachments.length} ${this.$tc(
        'attachment',
        this.data.attachments.length,
      ).toLocaleLowerCase()}`
    },
  },
  methods: {
    submit() {
      if (!this.validator.passes) return
      this.$emit('submit', this.getData())
    },
    onKeyEnter(e) {
      if (!e.metaKey) return
      this.submit()
    },
    onSuccess() {
      this.data.message = ''
      this.data.attachments = []
      this.$emit('onSuccess')
    },
    focus() {
      if (!this.$refs.input) return
      this.$refs.input.focus()
    },
  },
  components: {
    InputComponent,
    Tabs,
    FileComponent,
    DropdownComponent,
    Keyboard,
  },
}
</script>

<style lang="scss">
.comment-input {
  --gutter: 1em;

  .elder-input {
    position: relative;

    &__wrapper {
      align-items: flex-start;
    }

    &__field {
      margin: 0px;
      border: none;
    }

    &__element {
      resize: none;
      margin-bottom: 4em;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
    justify-content: end;
    position: absolute;
    right: var(--gutter);
    bottom: var(--gutter);
    padding-left: 8.5rem;
  }

  &__attachments {
    position: absolute;
    left: var(--gutter);
    bottom: var(--gutter);
  }

  .kvass-file {
    &__thumbnail {
      flex-wrap: nowrap;
    }
  }

  .tabs__header li > a {
    padding: 0.5em 1em;
  }
}
</style>
